import { RightEnum, Workflow } from "@finway-group/shared/lib/models"
import { Button, Empty } from "antd"
import React, { useCallback, useRef, useState } from "react"
import { Plus as PlusIcon } from "react-feather"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"

import { NoSearchDataFound } from "Components/NoSearchDataFound"
import { FilterLegacy } from "Components/filter/FilterLegacy"
import { FilterSectionLegacy } from "Components/filter/FilterSectionLegacy"
import Loading from "Components/loading"
import WorkflowModal from "Components/modals/workflow.modal"
import { PageTitle } from "Components/page/pageTitle"
import SearchInput from "Components/searchInput"
import { useCostCenters2 } from "Shared/hooks/costCenter2.hooks"
import { useSubCostCenters } from "Shared/hooks/costCenter.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { applyFilter } from "Shared/hooks/filter.hooks"
import { useFetchTable, useTable, useTableFilterAndSearchLoading } from "Shared/hooks/table.hooks"
import { useUserCreditors } from "Shared/hooks/vendor.hooks"
import { AuthzService } from "Shared/services"
import { updateTablePagination, updateTableSearch } from "Shared/store/actions/tables/tableActions"
import { setWorkflow } from "Shared/store/actions/workflow/workflowActions"
import { TablesEnum } from "Shared/store/reducers/tableConfigReducer"
import { isTableFilterApplied } from "Shared/utils/table.utils"

import WorkflowTable from "./workflow.table"

export const WorkflowsList = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const userCanCreateWorkflow = AuthzService.isRightGrantedForLoggedInUser(RightEnum.WORKFLOW__ALL__CREATE)

    const [isModalShowing, setIsModalShowing] = useState(false)

    const workflowsTable = useTable<Workflow>(TablesEnum.WORKFLOWS)
    const costCenters = useSubCostCenters(true)
    const userCreditors = useUserCreditors(AuthzService.isRightGrantedForLoggedInUser(RightEnum.EMPLOYEE__ALL__READ))
    const employees = useEmployees({ excludeDeleted: true })
    const costCenters2 = useCostCenters2()
    const isCreateBtnEnabled = costCenters.length > 0 || userCreditors.length > 0 || employees.length > 0
    const dragAndDropAnchor = useRef(null)

    const pageLoading = useTableFilterAndSearchLoading(TablesEnum.WORKFLOWS)

    useFetchTable(TablesEnum.WORKFLOWS)

    const handleSearch = useCallback(
        (value: string) => {
            dispatch(updateTablePagination(TablesEnum.WORKFLOWS, { current: 1, pageSize: workflowsTable.data.limit }))
            dispatch(updateTableSearch(TablesEnum.WORKFLOWS, value))
        },
        [dispatch],
    )

    return (
        <div>
            <PageTitle titleKey={"label:workflows"}>
                <SearchInput onSearch={handleSearch} value={workflowsTable.data.search} hidden={!(workflowsTable.data.totalDocs > 0 || workflowsTable.data.search)} />

                <FilterLegacy
                    // keep line breaks
                    table={TablesEnum.WORKFLOWS}
                    onFilter={(data) => applyFilter(TablesEnum.WORKFLOWS, data)}
                    options={{ userCreditors, costCenters, employees, costCenters2 }}
                />

                {userCanCreateWorkflow && (
                    <Button
                        data-testid="createWorkflowButton"
                        type="primary"
                        onClick={async () => {
                            await setWorkflow(undefined)(dispatch)
                            setIsModalShowing(true)
                        }}
                        disabled={!isCreateBtnEnabled}
                    >
                        <PlusIcon />
                        <span>{t("action:create_workflows")}</span>
                    </Button>
                )}
                <WorkflowModal isVisible={isModalShowing} onCancel={setIsModalShowing} />
            </PageTitle>
            <div id="drag-and-drop-anchor" ref={dragAndDropAnchor} className="w-full antd-table" />

            <FilterSectionLegacy table={TablesEnum.WORKFLOWS} options={{ userCreditors, costCenters, costCenters2, employees }} />

            {pageLoading ? (
                <Loading />
            ) : workflowsTable.data.totalDocs === 0 && !workflowsTable.isFetching ? (
                !workflowsTable.data.search ? (
                    <Empty
                        className="animation-appear"
                        image={`./icons/empty_table.svg`}
                        imageStyle={{
                            height: 150,
                            marginRight: "auto",
                            marginLeft: "auto",
                            marginTop: "15%",
                            marginBottom: "40px",
                            display: "inline-block",
                        }}
                        description={
                            <span className="text-lg font-bold mt-2 mb-0">
                                {!isTableFilterApplied(workflowsTable.data.filter) ? t("info:empty_state.workflows.title") : t("info:no_filter_data_found.title.workflow")}
                            </span>
                        }
                    >
                        <div className="flex flex-col justify-between items-center gap-y-4">
                            <p className="mb-14">
                                {!isTableFilterApplied(workflowsTable.data.filter) ? t("info:empty_state.workflows.message") : t("info:no_filter_data_found.message")}
                            </p>
                            {userCanCreateWorkflow && (
                                <Button
                                    type="primary"
                                    onClick={async () => {
                                        await setWorkflow(undefined)(dispatch)
                                        setIsModalShowing(true)
                                    }}
                                    disabled={!isCreateBtnEnabled}
                                >
                                    <PlusIcon />
                                    <span>{t("action:create_workflows")}</span>
                                </Button>
                            )}
                        </div>
                    </Empty>
                ) : (
                    <div className="animation-appear">
                        <NoSearchDataFound />
                    </div>
                )
            ) : (
                <WorkflowTable setIsModalShowing={setIsModalShowing} />
            )}
        </div>
    )
}
